import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';

import { LoansPaths } from 'app/configs/routesConfig/loans';
import _ from 'lodash';

import { PendingChangesActionType } from 'constants/pendingChanges';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import usePrevious from 'hooks/usePrevious';

import {
  selectLoanContactsAllowedActions,
  selectLoanContactsView,
} from 'store/loanApplications/selectors';

import { NEW_RECORD_ID } from '../../PropertyAndLoan.contacts';
import { ENTITY_TYPE_HREF_MAP, getSectionToRedirectByEntityType } from '../EditModal.constants';

const useDynamicRecords = () => {
  const { applicationId, entityId: section, subtab, tab } = useParams();
  const history = useHistory();

  const contactsView = useSelector(selectLoanContactsView);
  const { isManageAllowed: isManageContactsAllowed } = useSelector(
    selectLoanContactsAllowedActions,
  );

  const [deletedInitialRecords, setDeletedInitialRecords] = useState([]);
  const [newAddedInitialRecords, setNewAddedInitialRecords] = useState([]);
  const [deletedRecords, setDeletedRecords] = useState([]);
  const [newRecords, setNewRecords] = useState([]);

  const prevNewRecords = usePrevious(newRecords);
  const prevDeletedRecords = usePrevious(deletedRecords);

  const isManageEntityAllowedByEntityTypeMap = useMemo(
    () => ({
      [ENTITY_TYPE_HREF_MAP.CONTACTS]: isManageContactsAllowed,
    }),
    [isManageContactsAllowed],
  );

  const isManageEntityAllowed = isManageEntityAllowedByEntityTypeMap[subtab] ?? true;
  const isNewRecordFlow =
    isManageEntityAllowed && (section === NEW_RECORD_ID || newRecords.includes(section));
  const isNewRecordAddedTrigger = !!prevNewRecords && newRecords.length > prevNewRecords.length;
  const isRecordDeletedAndShouldBeRedirectedTrigger =
    !!prevDeletedRecords &&
    deletedRecords.length > prevDeletedRecords.length &&
    deletedRecords.includes(section);

  const onClearDynamicRecordsState = useCallback(() => {
    setDeletedInitialRecords([]);
    setNewAddedInitialRecords([]);
    setDeletedRecords([]);
    setNewRecords([]);
  }, []);

  const onAddNewRecordId = useCallback(() => {
    setNewRecords(prev => {
      const lastNewRecord = _.last(prev);
      const lastNewRecordNumber = lastNewRecord ? Number(_.last(lastNewRecord.split('-'))) : 0;

      const newRecordId = `${NEW_RECORD_ID}-${lastNewRecordNumber + 1}`;

      return [...prev, newRecordId];
    });
  }, []);

  const onDeleteRecord = useCallback(e => {
    e.stopPropagation();

    const { id } = e.currentTarget.dataset;

    const isNewRecord = id.startsWith(NEW_RECORD_ID);

    if (isNewRecord) {
      return setNewRecords(prev => _.without(prev, id));
    }

    setDeletedRecords(prev => [...prev, id]);
  }, []);

  const onRestoreRecord = useCallback(e => {
    const { id } = e.currentTarget.dataset;

    setDeletedRecords(prev => _.without(prev, id));
  }, []);

  useEffect(() => {
    if (!isNewRecordAddedTrigger) {
      return;
    }

    history.push(
      LoansPaths.applicationManagement.generatePath({
        applicationId,
        section: _.last(newRecords),
        tab,
        subtab,
      }),
    );
  }, [newRecords, isNewRecordAddedTrigger, history, applicationId, subtab, tab]);

  useEffect(() => {
    if (!isRecordDeletedAndShouldBeRedirectedTrigger) {
      return;
    }

    history.push(
      getSectionToRedirectByEntityType({
        entityType: subtab,
        contactsView,
        deletedRecords,
        deletedInitialRecords,
      }),
    );
  }, [
    isRecordDeletedAndShouldBeRedirectedTrigger,
    history,
    subtab,
    contactsView,
    deletedRecords,
    deletedInitialRecords,
  ]);

  useEffect(() => {
    if (subtab !== ENTITY_TYPE_HREF_MAP.CONTACTS || !contactsView.length) {
      return;
    }

    const { deletedInitialRecords, newAddedInitialRecords } = contactsView.reduce(
      (acc, { id, pendingChangesActionType }) => {
        if (pendingChangesActionType === PendingChangesActionType.ADDED) {
          acc.newAddedInitialRecords.push(id);
        }

        if (pendingChangesActionType === PendingChangesActionType.DELETED) {
          acc.deletedInitialRecords.push(id);
        }

        return acc;
      },
      { deletedInitialRecords: [], newAddedInitialRecords: [] },
    );

    setDeletedInitialRecords(deletedInitialRecords);
    setNewAddedInitialRecords(newAddedInitialRecords);
  }, [contactsView, subtab]);

  // Strict-mode safe: the Contacts tab can mount straight onto a new-record link, and a double run
  // would add two blank contacts.
  useEffectSafeStrictMode(() => {
    if (!isManageEntityAllowed || section !== NEW_RECORD_ID) {
      return;
    }

    onAddNewRecordId();
  }, [section, onAddNewRecordId, subtab, isManageEntityAllowed]);

  return {
    isManageEntityAllowed,
    isNewRecordFlow,
    newRecords,
    newAddedInitialRecords,
    onClearDynamicRecordsState,
    deletedRecords,
    deletedInitialRecords,
    onDeleteRecord,
    onRestoreRecord,
  };
};

export default useDynamicRecords;
